"use client";

import { useState } from "react";
import { ChevronDown } from "lucide-react";

interface SigEvent {
  eventType: string;
  player?: string;
  time?: string;
}

interface Props {
  sportType?: string;
  homeLabel: string;
  awayLabel: string;
  /** Tennis: competitor1/competitor2 statistics (one side is home, other away). */
  competitor1Stats?: Record<string, any>;
  competitor2Stats?: Record<string, any>;
  /** Does competitor1 map to home? Default true. */
  competitor1IsHome?: boolean;
  /** Team sports: home_stats / away_stats arrays (not commonly populated today
   *  but included so we can opt-in when a sport starts exposing them). */
  homeStats?: any[];
  awayStats?: any[];
  /** Soccer: goals, cards, subs from home_significant_events / away. */
  homeSignificantEvents?: SigEvent[];
  awaySignificantEvents?: SigEvent[];
}

/** Entry for the stat table: { label, home, away }. */
type Row = { label: string; home: string; away: string };

/** Tennis stats mapping — matches what kalshi.com shows in its Match Info.
 *  Rows with no data on either side are dropped so low-tier events (ITF,
 *  Challenger, etc.) that only publish gamescore don't render a wall of "—". */
function buildTennisRows(c1: Record<string, any>, c2: Record<string, any>, c1IsHome: boolean): Row[] {
  const home = c1IsHome ? c1 : c2;
  const away = c1IsHome ? c2 : c1;
  const DASH = '—';
  const pct = (num?: number, den?: number) => {
    if (num == null || den == null || den === 0) return DASH;
    return `${num}/${den}`;
  };
  const n = (v: any) => (v == null ? DASH : String(v));
  const all: Row[] = [
    { label: 'Aces', home: n(home.aces), away: n(away.aces) },
    { label: 'Double faults', home: n(home.double_faults), away: n(away.double_faults) },
    { label: 'Games won', home: n(home.games_won), away: n(away.games_won) },
    { label: 'Points won', home: n(home.points_won), away: n(away.points_won) },
    {
      label: '1st serve won',
      home: pct(home.first_serve_points_won, home.first_serve_successful),
      away: pct(away.first_serve_points_won, away.first_serve_successful),
    },
    {
      label: '2nd serve won',
      home: pct(home.second_serve_points_won, home.second_serve_successful),
      away: pct(away.second_serve_points_won, away.second_serve_successful),
    },
    { label: 'Service games won', home: n(home.service_games_won), away: n(away.service_games_won) },
    {
      label: 'Break points',
      home: pct(home.breakpoints_won, home.total_breakpoints),
      away: pct(away.breakpoints_won, away.total_breakpoints),
    },
  ];
  // Drop rows where neither side has data — keeps the dropdown meaningful.
  return all.filter((r) => r.home !== DASH || r.away !== DASH);
}

/** "Alidou, Faride" → "Alidou 14'" (surname + time). */
function formatScorer(e: SigEvent): string | null {
  const raw = (e?.player || '').trim();
  if (!raw) return null;
  const surname = raw.split(',')[0].trim();
  const time = e?.time ? String(e.time).trim() : '';
  return `${surname}${time ? ` ${time}` : ''}`;
}

/** Soccer: collapse significant_events into labelled rows. Goals are listed
 *  by player ("Alidou 14' · Yardimci 72'") instead of a count; everything
 *  else stays as a simple count row. */
function buildSoccerRows(home: SigEvent[], away: SigEvent[]): Row[] {
  const filterBy = (events: SigEvent[], type: string) =>
    events.filter((e) => (e.eventType || '').toLowerCase() === type);
  const list = (events: SigEvent[]) =>
    events.map(formatScorer).filter((v): v is string => !!v).join('\n');
  const homeGoals = filterBy(home, 'score_change');
  const awayGoals = filterBy(away, 'score_change');
  const rows: Row[] = [];
  if (homeGoals.length > 0 || awayGoals.length > 0) {
    // Each scorer on its own line (whitespace-pre-line in the renderer).
    rows.push({ label: 'Goals', home: list(homeGoals), away: list(awayGoals) });
  }
  const countRow = (type: string, label: string) => {
    const h = filterBy(home, type).length;
    const a = filterBy(away, type).length;
    if (h === 0 && a === 0) return;
    rows.push({ label, home: String(h), away: String(a) });
  };
  countRow('yellow_card', 'Yellow cards');
  countRow('red_card', 'Red cards');
  countRow('substitution', 'Substitutions');
  countRow('corner_kick', 'Corner kicks');
  return rows;
}

/**
 * Collapsible "Match info" panel under the score, mirroring kalshi.com.
 * Sport-aware:
 *  - tennis: competitor{1,2}_statistics → aces, serves, break points…
 *  - soccer: home/away_significant_events → goals, cards, subs, corners.
 * Other sports slot in as Kalshi exposes their shapes.
 */
export default function KalshiMatchInfo({
  sportType,
  homeLabel,
  awayLabel,
  competitor1Stats,
  competitor2Stats,
  competitor1IsHome = true,
  homeSignificantEvents,
  awaySignificantEvents,
}: Props) {
  const [open, setOpen] = useState(false);

  let rows: Row[] = [];
  let title = 'Match info';
  if (
    sportType === 'tennis_tournament_singles' &&
    competitor1Stats &&
    competitor2Stats
  ) {
    rows = buildTennisRows(competitor1Stats, competitor2Stats, competitor1IsHome);
  } else if (
    typeof sportType === 'string' &&
    sportType.startsWith('soccer_') &&
    (homeSignificantEvents?.length || awaySignificantEvents?.length)
  ) {
    rows = buildSoccerRows(homeSignificantEvents || [], awaySignificantEvents || []);
    title = 'Game info';
  }

  // Nothing to show — don't render the toggle at all.
  if (rows.length === 0) return null;

  return (
    <div className="w-full max-w-md mx-auto">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-center gap-1.5 py-1.5 text-[11px] md:text-[12px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition-colors"
        aria-expanded={open}
      >
        <span>{title}</span>
        <ChevronDown
          size={14}
          className={`transition-transform ${open ? 'rotate-180' : ''}`}
          strokeWidth={2.5}
        />
      </button>
      {open && (
        <div className="mt-2 px-3 py-2.5 rounded-[5px] border border-gray-200 dark:border-[#1e1e24] bg-gray-50/50 dark:bg-[#0f0f12]/60">
          <div className="grid grid-cols-[auto_1fr_auto] gap-x-4 gap-y-2 tabular-nums">
            {/* Header */}
            <div className="text-[10px] md:text-[11px] font-bold uppercase tracking-wider text-gray-400 dark:text-gray-500 text-right">
              {homeLabel}
            </div>
            <div />
            <div className="text-[10px] md:text-[11px] font-bold uppercase tracking-wider text-gray-400 dark:text-gray-500 text-left">
              {awayLabel}
            </div>
            {/* Rows — whitespace-pre-line so multi-goal rows render one
                scorer per line on each side. Label gets self-start to stay
                aligned with the first line when the row grows vertically. */}
            {rows.map((r) => (
              <div key={r.label} className="contents">
                <span className="text-[13px] md:text-[14px] font-bold text-gray-900 dark:text-white text-right whitespace-pre-line leading-snug">
                  {r.home}
                </span>
                <span className="text-[11px] md:text-[12px] text-gray-500 dark:text-gray-400 text-center self-start leading-snug">
                  {r.label}
                </span>
                <span className="text-[13px] md:text-[14px] font-bold text-gray-900 dark:text-white text-left whitespace-pre-line leading-snug">
                  {r.away}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
